import { ShapeInfo } from 'kld-intersections';
import { Geometry, IntersectResult } from '../../../platform/geom/Geometry';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Physical, RelativeTo } from '../entities/Physical';


export const Plots = {
    bounds(plots: Plot[]) {
        return Rect.fromMany(plots.map(x => x.bounds()));
    },
    centerTo(plots: Plot[], pt: Point) {
        const bounds = Plots.bounds(plots);
        const vector = Point.vector(pt, bounds.center());
        plots.forEach(p => p.moveBy(vector.x, vector.y));
        return plots;
    },
    nearestTo(plots: Plot[], target: Plot | Point): Plot {
        const pt = target instanceof Point ? target : target.center();

        let nearDist = Number.MAX_SAFE_INTEGER;
        let nearPlot = null as Plot;

        for (const p of plots) {
            const dist = p.center().distanceTo(pt);
            if (nearDist > dist) {
                nearDist = dist;
                nearPlot = p;
            }
        }

        return nearPlot;
    },
    farthestFrom(plots: Plot[], target: Plot | Point): Plot {
        const pt = target instanceof Point ? target : target.center();

        let farDist = Number.MIN_SAFE_INTEGER;
        let farPlot = null as Plot;

        for (const obj of plots) {
            const dist = obj.center().distanceTo(pt);
            if (farDist < dist) {
                farDist = dist;
                farPlot = obj;
            }
        }

        return farPlot;
    }
};

export interface Plot extends Geometry {

    get(metaProp: string): any;

    set(metaProp: string, value: any): void;

    left(): number;

    top(): number;

    width(): number;

    height(): number;

    topLeft(): Point;

    center(): Point;

    apply(): void;

    centerTo(x: number, y: number): this;

    moveTo(x: number, y: number): this;

    moveBy(x: number, y: number): this;

    intersects(another: Plot);

    intersectPoints(another: Geometry): IntersectResult;

    bounds(): Rect;

    toShapeInfo(): any;
}

export class PhysicalPlot implements Plot {

    public static for(elmt: Physical): PhysicalPlot {
        return new PhysicalPlot(elmt);
    }

    public static forAll(elmts: Physical[]): PhysicalPlot[] {
        return elmts.map(x => new PhysicalPlot(x));
    }

    private buffer: [number, number, number, number];
    private metadata?: Record<string, any>;

    public constructor(public readonly subject: Physical) {
        const bounds = subject.bounds(RelativeTo.Model);
        this.buffer = [bounds.left, bounds.top, bounds.width, bounds.height];
    }

    public get(metaProp: string) {
        return this.metadata?.[metaProp];
    }

    public set(metaProp: string, value: any) {
        this.metadata = this.metadata || {};
        this.metadata[metaProp] = value;
    }

    public left() {
        return this.buffer[0];
    }

    public top() {
        return this.buffer[1];
    }

    public width() {
        return this.buffer[2];
    }

    public height() {
        return this.buffer[3];
    }

    public topLeft() {
        return new Point(this.buffer[0], this.buffer[1]);
    }

    public center() {
        return this.topLeft().add([this.buffer[2] / 2, this.buffer[3] / 2]);
    }

    public apply() {
        const f = [...this.bounds().points(), ...this.subject.createFigure().slice(4)];
        this.subject.setFromFigure(f);
        // this.subject.moveTo(this.buffer[0], this.buffer[1]);
    }

    public centerTo(x: number, y: number) {
        this.buffer[0] = x - this.buffer[2] / 2;
        this.buffer[1] = y - this.buffer[3] / 2;
        return this;
    }

    public moveTo(x: number, y: number) {
        this.buffer[0] = x;
        this.buffer[1] = y;
        return this;
    }

    public moveBy(x: number, y: number) {
        this.buffer[0] += x;
        this.buffer[1] += y;
        return this;
    }

    // public resizeTo(w: number, h: number) {
    //     this.width = w;
    //     this.height = h;
    //     return this;
    // }

    // public resizeBy(w: number, h: number) {
    //     this.width += w;
    //     this.height += h;
    //     return this;
    // }

    public intersects(another: Plot) {
        return !!Geometry.intersect(this, another);
    }

    public intersectPoints(another: Geometry): IntersectResult {
        return Geometry.intersect(this, another);
    }

    public bounds(): Rect {
        return Rect.fromArray(this.buffer);
    }

    public toShapeInfo(): any {
        const { left, top, width, height } = this;
        return ShapeInfo.rectangle({ left, top, width, height });
    }
}


export class RectPlot implements Plot {

    public static for(rect: Rect): RectPlot {
        return new RectPlot(rect);
    }

    public static forAll(elmts: Rect[]): RectPlot[] {
        return elmts.map(x => new RectPlot(x));
    }

    private buffer: [number, number, number, number];
    private metadata?: Record<string, any>;

    public constructor(rect: Rect) {
        this.buffer = [rect.left, rect.top, rect.width, rect.height];
    }

    public get(metaProp: string) {
        return this.metadata?.[metaProp];
    }

    public set(metaProp: string, value: any) {
        this.metadata = this.metadata || {};
        this.metadata[metaProp] = value;
    }

    public left() {
        return this.buffer[0];
    }

    public top() {
        return this.buffer[1];
    }

    public width() {
        return this.buffer[2];
    }

    public height() {
        return this.buffer[3];
    }

    public topLeft() {
        return new Point(this.buffer[0], this.buffer[1]);
    }

    public center() {
        return this.topLeft().add([this.buffer[2] / 2, this.buffer[3] / 2]);
    }

    public apply() {

    }

    public moveTo(x: number, y: number) {
        this.buffer[0] = x;
        this.buffer[1] = y;
        return this;
    }

    public moveBy(x: number, y: number) {
        this.buffer[0] += x;
        this.buffer[1] += y;
        return this;
    }

    public centerTo(x: number, y: number): this {
        this.buffer[0] = x - this.buffer[2] / 2;
        this.buffer[1] = y - this.buffer[3] / 2;
        return this;
    }

    // public resizeTo(w: number, h: number) {
    //     this.width = w;
    //     this.height = h;
    //     return this;
    // }

    // public resizeBy(w: number, h: number) {
    //     this.width += w;
    //     this.height += h;
    //     return this;
    // }

    public intersects(another: Plot) {
        return !!Geometry.intersect(this, another);
    }

    public intersectPoints(another: Geometry): IntersectResult {
        return Geometry.intersect(this, another);
    }

    public bounds(): Rect {
        return Rect.fromArray(this.buffer);
    }

    public toRect(): Rect {
        return Rect.fromArray(this.buffer);
    }

    public toShapeInfo(): any {
        const { left, top, width, height } = this;
        return ShapeInfo.rectangle({ left, top, width, height });
    }
}
